import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Empty, Tooltip, message } from 'antd';
import {
    CodeOutlined,
    DeleteOutlined,
    DragOutlined,
    FontSizeOutlined,
    ImportOutlined,
    FileTextOutlined,
    LineOutlined,
    PictureOutlined,
    TableOutlined,
    TagOutlined,
} from '@ant-design/icons';
import {
    BUILDER_BLOCK_TYPES,
    createBuilderBlock,
    serializeBlockToHtml,
    type BuilderBlockType,
} from '../domain/visualBuilderBlocks';
import type { VariableGroup } from './RichTextEditor';

interface VisualHtmlDocumentBuilderProps {
    value: string;
    cssContent?: string | null;
    onChange: (html: string) => void;
    variableGroups: VariableGroup[];
    disabled?: boolean;
}

const BLOCK_TYPE_DATA = 'application/x-builder-block-type';
const BUILDER_SELECTED_ATTRIBUTE = 'data-builder-selected';

const BLOCK_ICONS: Record<BuilderBlockType, JSX.Element> = {
    heading: <FontSizeOutlined />,
    paragraph: <FileTextOutlined />,
    image: <PictureOutlined />,
    table: <TableOutlined />,
    divider: <LineOutlined />,
    verticalLine: <LineOutlined rotate={90} />,
    variable: <TagOutlined />,
    html: <CodeOutlined />,
};

const SIDE_PANEL_STYLE: CSSProperties = {
    width: 220,
    flexShrink: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
};
const PALETTE_STYLE: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8 };
const PALETTE_ITEM_STYLE: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '8px 12px',
    border: '1px dashed #d9d9d9',
    borderRadius: 4,
    cursor: 'grab',
    background: '#fafafa',
    textAlign: 'left',
    font: 'inherit',
    color: 'inherit',
};
const CANVAS_STYLE: CSSProperties = {
    flex: 1,
    minWidth: 0,
    background: '#f0f2f5',
    borderRadius: 4,
    overflow: 'auto',
    padding: 24,
};
const FRAME_STYLE: CSSProperties = {
    width: '100%',
    minWidth: 860,
    height: 'calc(75vh - 52px)',
    border: '2px solid transparent',
    borderRadius: 2,
    background: '#fff',
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
};

const escapeStyleContent = (value: string): string => value.replace(/<\/style/gi, '<\\/style');

const buildFrameDocument = (
    htmlContent: string,
    cssContent?: string | null,
): string => `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<style>${escapeStyleContent(cssContent ?? '')}</style>
<style>
html, body { min-height: 100%; }
body { margin: 24px; cursor: default; }
*[${BUILDER_SELECTED_ATTRIBUTE}="true"] {
    outline: 2px solid #1677ff !important;
    outline-offset: 2px !important;
}
*[data-builder-added="true"] { cursor: move; }
</style>
</head>
<body>${htmlContent}</body>
</html>`;

const serializeFrameBody = (doc: Document): string => {
    const clone = doc.body.cloneNode(true) as HTMLElement;
    clone.querySelectorAll(`[${BUILDER_SELECTED_ATTRIBUTE}]`).forEach(element => {
        element.removeAttribute(BUILDER_SELECTED_ATTRIBUTE);
    });
    clone.querySelectorAll('[data-builder-added]').forEach(element => {
        element.removeAttribute('data-builder-added');
    });
    return clone.innerHTML;
};

const elementLabel = (element: HTMLElement | null): string => {
    if (!element) return '';
    const className = element.className
        ? `.${String(element.className).trim().split(/\s+/).join('.')}`
        : '';
    return `${element.tagName.toLowerCase()}${className}`;
};

const pixelNumber = (value: string): number => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const VisualHtmlDocumentBuilder = ({
    value,
    cssContent,
    onChange,
    variableGroups,
    disabled,
}: VisualHtmlDocumentBuilderProps) => {
    const { t } = useTranslation();
    const frameRef = useRef<HTMLIFrameElement | null>(null);
    const selectedElementRef = useRef<HTMLElement | null>(null);
    const lastEmitted = useRef<string | null>(null);
    const [frameHtml, setFrameHtml] = useState(() => buildFrameDocument(value, cssContent));
    const [selectedElementLabel, setSelectedElementLabel] = useState('');
    const [dragOver, setDragOver] = useState(false);

    const firstVariableToken = useMemo(
        () => variableGroups.flatMap(group => group.variables)[0]?.token,
        [variableGroups],
    );

    useEffect(() => {
        if (value === lastEmitted.current) return;
        selectedElementRef.current = null;
        setSelectedElementLabel('');
        setFrameHtml(buildFrameDocument(value, cssContent));
    }, [cssContent, value]);

    const emitFrameHtml = useCallback(() => {
        const doc = frameRef.current?.contentDocument;
        if (!doc) return;
        const html = serializeFrameBody(doc);
        lastEmitted.current = html;
        onChange(html);
    }, [onChange]);

    const clearSelection = useCallback(() => {
        selectedElementRef.current?.removeAttribute(BUILDER_SELECTED_ATTRIBUTE);
        selectedElementRef.current = null;
        setSelectedElementLabel('');
    }, []);

    const selectElement = useCallback(
        (element: HTMLElement | null) => {
            if (!element) {
                clearSelection();
                return;
            }
            if (element === selectedElementRef.current) return;
            selectedElementRef.current?.removeAttribute(BUILDER_SELECTED_ATTRIBUTE);
            element.setAttribute(BUILDER_SELECTED_ATTRIBUTE, 'true');
            selectedElementRef.current = element;
            setSelectedElementLabel(elementLabel(element));
        },
        [clearSelection],
    );

    const createElementForBlock = useCallback(
        (doc: Document, type: BuilderBlockType): HTMLElement => {
            const block = createBuilderBlock(
                type,
                type === 'variable' && firstVariableToken ? firstVariableToken : undefined,
            );
            const template = doc.createElement('template');
            template.innerHTML = serializeBlockToHtml(block).trim();
            const element = template.content.firstElementChild;
            const HtmlElement = doc.defaultView?.HTMLElement;
            if (HtmlElement && element instanceof HtmlElement) return element;

            const fallback = doc.createElement('p');
            fallback.textContent = block.content;
            return fallback;
        },
        [firstVariableToken],
    );

    const addBlockToDocument = useCallback(
        (type: BuilderBlockType, left = 32, top = 32) => {
            if (disabled) return;
            const doc = frameRef.current?.contentDocument;
            if (!doc?.body) return;
            const element = createElementForBlock(doc, type);
            element.setAttribute('data-builder-added', 'true');
            element.style.position = 'absolute';
            element.style.left = `${Math.max(0, Math.round(left))}px`;
            element.style.top = `${Math.max(0, Math.round(top))}px`;
            if (type === 'heading' || type === 'paragraph' || type === 'variable') {
                element.style.minWidth = '120px';
            }
            doc.body.append(element);
            selectElement(element);
            emitFrameHtml();
        },
        [createElementForBlock, disabled, emitFrameHtml, selectElement],
    );

    const deleteSelectedElement = useCallback(() => {
        if (disabled) return;
        const element = selectedElementRef.current;
        if (!element) return;
        element.remove();
        selectedElementRef.current = null;
        setSelectedElementLabel('');
        emitFrameHtml();
        message.success(t('configuration.invoiceDocument.builder.deleteDone'));
    }, [disabled, emitFrameHtml, t]);

    const handlePaletteDragStart =
        (type: BuilderBlockType) => (event: DragEvent<HTMLButtonElement>) => {
            event.dataTransfer.setData(BLOCK_TYPE_DATA, type);
            event.dataTransfer.effectAllowed = 'copy';
        };

    const reloadTemplate = () => {
        clearSelection();
        lastEmitted.current = null;
        setFrameHtml(buildFrameDocument(value, cssContent));
        if (!value.trim()) {
            message.info(t('configuration.invoiceDocument.builder.importEmpty'));
            return;
        }
        message.success(t('configuration.invoiceDocument.builder.importDone'));
    };

    const handleFrameLoad = () => {
        const frame = frameRef.current;
        const doc = frame?.contentDocument;
        const win = frame?.contentWindow;
        if (!doc || !win) return;

        let dragState: {
            element: HTMLElement;
            startX: number;
            startY: number;
            startLeft: number;
            startTop: number;
            moved: boolean;
        } | null = null;

        const finishDrag = () => {
            if (dragState?.moved) emitFrameHtml();
            dragState = null;
        };

        doc.addEventListener('click', event => {
            if (disabled) return;
            const target = event.target as HTMLElement | null;
            if (!target || target.nodeType !== 1 || target === doc.body) {
                clearSelection();
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            selectElement(target);
        });

        doc.addEventListener('mousedown', event => {
            if (disabled || event.button !== 0) return;
            const target = event.target as HTMLElement | null;
            if (!target || target.nodeType !== 1 || target === doc.body) return;
            selectElement(target);
            const computed = win.getComputedStyle(target);
            if (computed.position === 'static') {
                target.style.position = 'relative';
            }
            dragState = {
                element: target,
                startX: event.clientX,
                startY: event.clientY,
                startLeft: pixelNumber(target.style.left || computed.left),
                startTop: pixelNumber(target.style.top || computed.top),
                moved: false,
            };
        });

        doc.addEventListener('mousemove', event => {
            if (!dragState) return;
            const deltaX = event.clientX - dragState.startX;
            const deltaY = event.clientY - dragState.startY;
            if (Math.abs(deltaX) + Math.abs(deltaY) < 2) return;
            dragState.moved = true;
            dragState.element.style.left = `${Math.round(dragState.startLeft + deltaX)}px`;
            dragState.element.style.top = `${Math.round(dragState.startTop + deltaY)}px`;
        });

        doc.addEventListener('mouseup', finishDrag);
        doc.addEventListener('mouseleave', finishDrag);

        doc.addEventListener('dragover', event => {
            if (disabled) return;
            event.preventDefault();
            setDragOver(true);
        });
        doc.addEventListener('dragleave', () => setDragOver(false));
        doc.addEventListener('drop', event => {
            if (disabled) return;
            event.preventDefault();
            setDragOver(false);
            const type = event.dataTransfer?.getData(BLOCK_TYPE_DATA) as BuilderBlockType;
            if (!BUILDER_BLOCK_TYPES.includes(type)) return;
            addBlockToDocument(type, event.pageX, event.pageY);
        });
        doc.addEventListener('keydown', event => {
            if (disabled) return;
            if (event.key === 'Delete' || event.key === 'Backspace') deleteSelectedElement();
        });
    };

    return (
        <div style={{ display: 'flex', gap: 16, height: '75vh' }}>
            <div style={SIDE_PANEL_STYLE}>
                <Alert
                    type="warning"
                    showIcon
                    message={t('configuration.invoiceDocument.builder.replaceWarning')}
                />
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <strong>{t('configuration.invoiceDocument.builder.paletteTitle')}</strong>
                    <Tooltip title={t('configuration.invoiceDocument.builder.importHint')}>
                        <Button
                            size="small"
                            icon={<ImportOutlined />}
                            disabled={disabled}
                            onClick={reloadTemplate}
                        >
                            {t('configuration.invoiceDocument.builder.import')}
                        </Button>
                    </Tooltip>
                </div>
                <div style={{ color: '#888', fontSize: 12 }}>
                    {t('configuration.invoiceDocument.builder.paletteHint')}
                </div>
                <div style={PALETTE_STYLE}>
                    {BUILDER_BLOCK_TYPES.map(type => (
                        <button
                            key={type}
                            type="button"
                            draggable={!disabled}
                            disabled={disabled}
                            onClick={() => addBlockToDocument(type)}
                            onDragStart={handlePaletteDragStart(type)}
                            style={PALETTE_ITEM_STYLE}
                        >
                            {BLOCK_ICONS[type]}
                            <span>{t(`configuration.invoiceDocument.builder.blocks.${type}`)}</span>
                        </button>
                    ))}
                </div>
                <Alert
                    type={selectedElementLabel ? 'info' : 'warning'}
                    showIcon
                    icon={<DragOutlined />}
                    message={
                        selectedElementLabel ||
                        t('configuration.invoiceDocument.builder.noSelection')
                    }
                    description={t('configuration.invoiceDocument.builder.moveHint')}
                />
                <Tooltip title={t('configuration.invoiceDocument.builder.remove')}>
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        disabled={disabled || !selectedElementLabel}
                        onClick={deleteSelectedElement}
                    >
                        {t('configuration.invoiceDocument.builder.remove')}
                    </Button>
                </Tooltip>
            </div>
            <section
                aria-label={t('configuration.invoiceDocument.builder.canvasTitle')}
                style={{
                    ...CANVAS_STYLE,
                    border: dragOver ? '2px dashed #1677ff' : '2px solid transparent',
                }}
            >
                {!value.trim() ? (
                    <Empty
                        description={t('configuration.invoiceDocument.builder.canvasEmpty')}
                        style={{ paddingTop: 80 }}
                    />
                ) : null}
                <iframe
                    ref={frameRef}
                    title={t('configuration.invoiceDocument.builder.canvasTitle')}
                    srcDoc={frameHtml}
                    sandbox="allow-same-origin"
                    style={{ ...FRAME_STYLE, display: value.trim() ? 'block' : 'none' }}
                    onLoad={handleFrameLoad}
                />
            </section>
        </div>
    );
};

export default VisualHtmlDocumentBuilder;
